import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { userLoggedOut } from "app/redux/auth/authSlice";
import { BASE_URL } from "app/utils/axiosInstance";
import { getCurrentUser } from "app/services/company/userService";
import { io } from "socket.io-client";
import Swal from "sweetalert2";

export default function useSessionSecurity() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, accessToken, user } = useSelector((state) => state.auth);
  const userId = user?.id || user?._id;
  const isTerminatingRef = useRef(false);

  const handleForcedLogout = (reasonMessage) => {
    if (isTerminatingRef.current) return;
    isTerminatingRef.current = true;

    const notice =
      reasonMessage ||
      "Votre compte a été bloqué par un administrateur. Votre session a été clôturée.";

    try {
      sessionStorage.setItem("blocked_account_message", notice);
      // Remove auth_user from storage immediately so background requests cease
      localStorage.removeItem("auth_user");
    } catch (e) {
      // ignore storage errors
    }

    // Display modal for 6 seconds (or until user clicks) BEFORE unmounting and redirecting
    Swal.fire({
      icon: "error",
      title: "Compte suspendu",
      html: `
        <div style="font-size: 14px; color: #334155; margin-bottom: 12px; line-height: 1.5;">
          ${notice}
        </div>
        <div style="font-size: 12px; color: #94a3b8;">
          Vous allez être déconnecté automatiquement dans quelques secondes...
        </div>
      `,
      timer: 6000,
      timerProgressBar: true,
      showConfirmButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Se déconnecter maintenant",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      // Log out in Redux and navigate only after modal has been acknowledged or timer expires
      dispatch(userLoggedOut());
      navigate("/sessions/signin?blocked=1", { replace: true });
    });
  };

  // Expose handler globally so axios interceptor can trigger the modal instead of an instant hard redirect
  useEffect(() => {
    window.__handleForcedLogout = handleForcedLogout;
    return () => {
      if (window.__handleForcedLogout === handleForcedLogout) {
        window.__handleForcedLogout = null;
      }
    };
  }, []);

  // 1. WebSocket real-time listener
  useEffect(() => {
    if (!isAuthenticated || !accessToken || !userId) return;

    let socket = null;
    try {
      socket = io(`${BASE_URL}/chat`, {
        auth: { token: accessToken },
        transports: ["websocket"],
      });

      socket.on("connect", () => {
        // Connected to user room
      });

      socket.on("account_blocked", (data) => {
        console.warn("Received account_blocked socket event:", data);
        handleForcedLogout(data?.message);
      });
    } catch (err) {
      console.warn("Could not connect security socket:", err);
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [isAuthenticated, accessToken, userId]);

  // 2. Active status heartbeat + focus/visibility check
  useEffect(() => {
    if (!isAuthenticated || !accessToken || !userId) return;

    const checkStatus = async () => {
      if (isTerminatingRef.current) return;
      try {
        const freshUser = await getCurrentUser();
        if (freshUser?.isBlocked) {
          handleForcedLogout("Ce compte a été suspendu par un administrateur.");
        }
      } catch (err) {
        const status = err?.response?.status;
        const code = err?.response?.data?.code;
        const msg = err?.response?.data?.message;
        if (
          status === 403 &&
          (code === "ACCOUNT_BLOCKED" ||
            (typeof msg === "string" &&
              (msg.toLowerCase().includes("suspendu") || msg.toLowerCase().includes("bloqu"))))
        ) {
          handleForcedLogout(msg);
        }
      }
    };

    // Heartbeat check every 10 seconds
    const interval = setInterval(checkStatus, 10000);

    // Immediate check on tab focus or visibility change
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkStatus();
      }
    };
    window.addEventListener("focus", checkStatus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", checkStatus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isAuthenticated, accessToken, userId]);
}
